import { DataTable, TableMenu } from '@/components'
import type { TableMenuItem } from '@/components'
import { cn } from '@/lib/utils'
import type { VehicleServicePlanNode } from '@/modules/vehicles'
import { PERMISSIONS, usePermissions } from '@/utils'
import { format, formatDistanceStrict } from '@/utils/date-fns'
import { createColumnHelper } from '@tanstack/react-table'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo } from 'react'
import { Link } from 'react-router'
import { ServicePlanStatusBadge } from './service-plan-status'

interface Props {
  servicePlans: VehicleServicePlanNode[]
  loading?: boolean
  onEdit(servicePlan: VehicleServicePlanNode): void
  onDelete(servicePlan: VehicleServicePlanNode): void
  onMoveToExpirations(servicePlan: VehicleServicePlanNode): void
}

export const VehicleServicePlansTable: React.FC<Props> = ({
  servicePlans,
  loading = false,
  onEdit,
  onDelete,
  onMoveToExpirations,
}) => {
  const translations = useTranslate({
    vehicle: 'common.vehicle',
    name: 'common.name',
    type: 'common.type',
    scheduled: 'vehicles.services.scheduled',
    endDate: 'common.end-date',
    status: 'common.status',
    workshop: 'vehicles.service.workshop',
    notificationTime: 'common.notification',
    responsibleUser: 'common.responsible-user',
    noServices: 'vehicles.no-vehicle-services',
    edit: 'common.edit',
    delete: 'common.delete',
    moveToExpirations: 'vehicles.services.move-to-expirations',
    items: ['vehicles.services.num-services', { n: 0 }],
  })

  const { hasPermissions } = usePermissions()
  // Services reuse the expiration permissions: the two lived in one table until
  // FD-571, so nobody had to be re-granted anything by the split.
  const canEdit = hasPermissions(
    PERMISSIONS.vehicles.change.vehicleexpiration,
    true
  )
  const canDelete = hasPermissions(
    PERMISSIONS.vehicles.delete.vehicleexpiration,
    true
  )
  const canAdd = hasPermissions(
    PERMISSIONS.vehicles.add.vehicleexpiration,
    true
  )

  const column = createColumnHelper<VehicleServicePlanNode>()

  const columns = useMemo(
    () => [
      column.accessor('vehicle', {
        header: translations.vehicle,
        meta: { narrow: true },
        enableSorting: true,
        sortingFn: (a, b) =>
          (a.original.vehicle?.registrationNumber ?? '').localeCompare(
            b.original.vehicle?.registrationNumber ?? ''
          ),
        cell(info) {
          const vehicle = info.getValue()
          if (!vehicle) return '-'

          return (
            <Link className="text-inherit" to={`/vehicle-fleet/${vehicle.id}`}>
              {vehicle.registrationNumber || '-'}
            </Link>
          )
        },
      }),
      column.accessor('name', {
        header: translations.name,
        enableSorting: true,
        cell: info => info.getValue() || '-',
      }),
      column.accessor('serviceType', {
        header: translations.type,
        cell: info => info.getValue()?.name || '-',
      }),
      column.accessor('scheduledStart', {
        header: translations.scheduled,
        meta: { narrow: true },
        enableSorting: true,
        sortingFn: (a, b) =>
          new Date(a.original.scheduledStart).getTime() -
          new Date(b.original.scheduledStart).getTime(),
        cell(info) {
          const scheduledStart = info.getValue()
          if (!scheduledStart) return '-'
          return format(new Date(scheduledStart), 'PPp')
        },
      }),
      column.accessor('scheduledEnd', {
        header: translations.endDate,
        meta: { narrow: true },
        cell(info) {
          const scheduledEnd = info.getValue()
          if (!scheduledEnd) return '-'
          return format(new Date(scheduledEnd), 'PPp')
        },
      }),
      column.display({
        id: 'status',
        header: translations.status,
        meta: { narrow: true },
        cell: info => (
          <ServicePlanStatusBadge status={info.row.original.status} />
        ),
      }),
      column.accessor('workshopName', {
        header: translations.workshop,
        cell: info => info.getValue() || '-',
      }),
      column.accessor('notificationTime', {
        header: translations.notificationTime,
        meta: { narrow: true },
        cell(info) {
          const { scheduledStart } = info.row.original
          const notificationTime = info.getValue()
          if (!notificationTime || !scheduledStart) return '-'

          return formatDistanceStrict(
            new Date(scheduledStart),
            new Date(notificationTime)
          )
        },
      }),
      column.accessor('responsibleUser', {
        header: translations.responsibleUser,
        meta: { narrow: true },
        enableSorting: true,
        sortingFn: (a, b) =>
          (a.original.responsibleUser?.fullName ?? '').localeCompare(
            b.original.responsibleUser?.fullName ?? ''
          ),
        cell: info => info.getValue()?.fullName || '-',
      }),
      column.display({
        id: 'menu',
        header: '',
        meta: { narrow: true, center: true },
        cell(info) {
          const servicePlan = info.row.original
          // An integration rewrites its own rows on every sync, so editing,
          // deleting or moving one only holds until the next run.
          const fromIntegration = servicePlan.source !== 'MANUAL'

          const items: TableMenuItem[] = []
          if (canEdit && !fromIntegration) {
            items.push({
              id: 'edit',
              type: 'regular',
              label: translations.edit,
              icon: 'edit',
              onClick: () => onEdit(servicePlan),
            })
          }
          if (canAdd && canDelete && !fromIntegration) {
            items.push({
              id: 'move-to-expirations',
              type: 'regular',
              label: translations.moveToExpirations,
              icon: 'calendar-day',
              onClick: () => onMoveToExpirations(servicePlan),
            })
          }
          if (canDelete && !fromIntegration) {
            items.push({
              id: 'delete',
              type: 'regular',
              label: translations.delete,
              icon: 'trash',
              onClick: () => onDelete(servicePlan),
            })
          }
          if (items.length === 0) return null

          return <TableMenu items={items} />
        },
      }),
    ],
    [
      canAdd,
      canDelete,
      canEdit,
      column,
      onDelete,
      onEdit,
      onMoveToExpirations,
      translations.delete,
      translations.edit,
      translations.endDate,
      translations.moveToExpirations,
      translations.name,
      translations.notificationTime,
      translations.responsibleUser,
      translations.scheduled,
      translations.status,
      translations.type,
      translations.vehicle,
      translations.workshop,
    ]
  )

  return (
    <DataTable
      className={cn('w-full')}
      columns={columns}
      data={servicePlans}
      loading={loading}
      inCard
      initialSort={[{ id: 'scheduledStart', desc: false }]}
      noDataText={translations.noServices}
      pagination={{ itemsText: n => translations.items({ n }) }}
      rowClassName={row =>
        row.status === 'CANCELLED' ? 'text-stone-400 line-through' : undefined
      }
    />
  )
}
